import { Box, Center, useBreakpoint } from '@chakra-ui/react';
// eslint-disable-next-line no-restricted-imports
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Loader from '../../components/Loader';
import Title from '../../components/Title';
import LicensePlateOrVinInput from '../../components/licensePlateOrVinInput/LicensePlateOrVinInput';
import ContinueContainer from '../../components/ui/GetStarted/ContinueContainer';
import { isProd } from '../../config';
import { COUNTRY_CONFIG } from '../../constants/countries';
import { METHODFI_FLOW_VARIATIONS } from '../../constants/methodfi';
import { useCreateTemporaryInfoMutation } from '../../gql/generated/graphql';
import { useFlowCountry, useStep } from '../../hooks';
import useCheckFlowStatus from '../../hooks/useCheckFlowStatus';
import { CookieKeys, useCookie } from '../../hooks/useCookie';
import { trackFlowStart } from '../../services/analytics/conversionEvents';
import FlowEntryDevToolbar from './components/FlowEntryDevToolbar';
import { getExtraInfo, getFlowParams } from './utils';

const FlowEntry = () => {
  const { search } = useLocation();
  const country = useFlowCountry();
  const { plateLookup } = COUNTRY_CONFIG[country];
  const urlFlowParams = getFlowParams(search);
  const flowParams = plateLookup
    ? urlFlowParams
    : { ...urlFlowParams, licensePlate: undefined, licensePlateState: undefined };
  const startPrompt = plateLookup
    ? 'Enter your license plate number and state or VIN to get started'
    : 'Enter your VIN to get started';
  const [initialized, setInitialized] = useState(false);

  const methodFiEnabled =
    flowParams.methodFiVariation != null &&
    flowParams.methodFiVariation !== METHODFI_FLOW_VARIATIONS.off;
  const device = useBreakpoint();
  const step = useStep();
  const ldClient = useLDClient();
  const [, setGuid] = useCookie<string>(CookieKeys.GUID_KEY);
  const [createTempInfo] = useCreateTemporaryInfoMutation({
    context: { isErrorHandled: true },
  });
  const hasStartedRef = useRef(false);

  const hasData = !!flowParams.vin || (!!flowParams.licensePlate && !!flowParams.licensePlateState);
  const isMethodFiEntry =
    methodFiEnabled &&
    !!flowParams.zip &&
    !!flowParams.firstName &&
    !!flowParams.lastName &&
    !hasData;

  const {
    inFlow,
    enterFlow,
    canStartOver,
    startOver,
    loading: flowCheckLoading,
  } = useCheckFlowStatus();

  useEffect(() => {
    const startMethodFiFlow = async () => {
      if (hasStartedRef.current || inFlow || flowCheckLoading) return;
      hasStartedRef.current = true;

      try {
        const result = await createTempInfo({
          variables: {
            device_type: device,
            extra_input: getExtraInfo(country, flowParams),
            query_params: search,
            is_methodfi_flow: true,
            methodfi_flow_variation: flowParams.methodFiVariation,
          },
        });

        const id = result.data?.createTemporaryInfo?.id;
        const tempInfoData = result.data?.createTemporaryInfo?.data;

        if (id) {
          trackFlowStart(ldClient, id, search, tempInfoData);
          setGuid(id);
          step.moveNext({}, true);
        } else {
          setInitialized(true);
        }
      } catch {
        setInitialized(true);
      }
    };

    if (isMethodFiEntry && !flowCheckLoading) {
      startMethodFiFlow();
    }
  }, [isMethodFiEntry, flowCheckLoading, inFlow]);

  useEffect(() => {
    if (inFlow && !flowCheckLoading) {
      setInitialized(true);
    }
  }, [inFlow, flowCheckLoading]);

  return (
    <Box mt="80px">
      {!flowCheckLoading && initialized && !isMethodFiEntry && (
        <Title fontSize={{ base: '36px', md: '52px' }} px={{ base: 4, md: 0 }}>
          {inFlow
            ? 'Seems you already started the lease end process, continue by clicking on the button below'
            : hasData
            ? "We've encountered an issue"
            : startPrompt}
        </Title>
      )}
      <Center mb="30px" mt={inFlow ? '50px' : '30px'}>
        {flowCheckLoading || (isMethodFiEntry && !initialized) ? (
          <Loader />
        ) : inFlow ? (
          <ContinueContainer
            enterFlow={enterFlow}
            canStartOver={canStartOver}
            startOver={startOver}
          />
        ) : (
          <LicensePlateOrVinInput
            autoFocus
            flowParams={flowParams}
            referral={{
              referrer_id:
                flowParams.referrerId != null ? Number(flowParams.referrerId) || null : null,
              code: flowParams.referralCode || null,
            }}
            onReady={() => setInitialized(true)}
          />
        )}
      </Center>
      {!isProd && <FlowEntryDevToolbar />}
    </Box>
  );
};

export default FlowEntry;
